import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { ListResponseOfSkuRelationshipType } from "services/KnowfullyAdminClient";
import { IdValue } from "types/common-types";

type SelectSkuRelationshipProps = {
  value?: string | null;
  onChange: (id: string | null) => void;
  onDirty?: () => void;
  onClean?: () => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<IdValue>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectSkuRelationshipType = ({
  value: skuRelationshipTypeId,
  onChange,
  onDirty,
  onClean,
  ...rest
}: SelectSkuRelationshipProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [
    skuRelationshipType,
    setSkuRelationshipType,
  ] = useState<IdValue | null>(null);

  const { isLoading, data } = useQuery<ListResponseOfSkuRelationshipType>(
    ["skuRelationshipType", skuRelationshipTypeId],
    () => skuService.getSkuRelationshipTypes(),
    {
      enabled:
        !!skuRelationshipTypeId &&
        (!skuRelationshipType ||
          skuRelationshipType.id !== skuRelationshipTypeId),
    },
  );

  useEffect(() => {
    if (data?.data?.[0]) {
      setSkuRelationshipType({
        id: data.data[0].id,
        value: data.data[0].name ?? "?",
      });
    }
  }, [data, skuRelationshipType]);

  const fetchSkuRelationshipTypes = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfSkuRelationshipType>(
      ["relationships", searchTerm],
      () => skuService.getSkuRelationshipTypes(),
    );

    const levels = {
      ...result,
      data: (result.data ?? []).map(({ id, name }) => ({
        id,
        value: name ?? "?",
      })),
    };

    return { result: levels, forSearchTerm: searchTerm };
  };

  const onChangeSkuRelationship = (skuRelationshipType: IdValue | null) => {
    setSkuRelationshipType(skuRelationshipType);
    onChange(skuRelationshipType?.id ?? null);
    if (skuRelationshipType !== null) {
      onDirty && onDirty();
    } else {
      onClean && onClean();
    }
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={skuRelationshipType}
      isLoadingInitialValue={isLoading}
      labelField="value"
      idField="id"
      fetchData={fetchSkuRelationshipTypes}
      failMessage={t({ id: "selectSkuRelationshipFetchFailed" })}
      onChange={onChangeSkuRelationship}
    />
  );
};

export default SelectSkuRelationshipType;
